/*
  Cookie consent, on top of Shopify's Customer Privacy API.

  The API is loaded on demand — it is not part of the initial payload, and
  a visitor who already chose never pays for it beyond one small script.
  If the API fails to load we do NOT show the banner: a banner whose
  buttons cannot record a decision is worse than none.
*/
class AmvConsent extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.amv-consent__panel');
    this.detail = this.querySelector('.amv-consent__detail');
    this.toggle = this.querySelector('[data-amv-consent-toggle]');

    this.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-amv-consent-open]')) {
        e.preventDefault();
        this.open(true);
      }
    });

    this.load();
  }

  load() {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) return;
      this.api = window.Shopify.customerPrivacy;
      if (!this.api) return;
      this.reflect();
      // Alegerea deja facuta trebuie retrimisa la Google pe fiecare pagina:
      // layout-ul porneste mereu cu `denied`.
      if (this.decided()) {
        const c = this.api.currentVisitorConsent();
        this.google({ analytics: c.analytics === 'yes', marketing: c.marketing === 'yes', preferences: c.preferences === 'yes' });
        return;
      }
      if (!this.api.shouldShowBanner || !this.api.shouldShowBanner()) return;
      // API-ul a uitat alegerea, dar vizitatorul a facut-o: o reaplicam
      // in loc sa-l intrebam din nou.
      const saved = this.remembered();
      if (saved) {
        this.api.setTrackingConsent(saved, () => {});
        this.google(saved);
        return;
      }
      this.open(false);
    });
  }

  /*
    `shouldShowBanner()` spune doar ca regiunea cere banner, nu ca
    vizitatorul n-a ales inca: ramane `true` si dupa „Accept toate”. Luat
    singur, redeschidea bannerul pe fiecare pagina. Alegerea facuta se
    vede in `currentVisitorConsent()` — o categorie fara raspuns e ''.
  */
  decided() {
    if (typeof this.api.currentVisitorConsent !== 'function') return false;
    const c = this.api.currentVisitorConsent() || {};
    return ['analytics', 'marketing', 'preferences'].every((k) => c[k] === 'yes' || c[k] === 'no');
  }

  /*
    Copie locala a alegerii. Shopify o tine intr-un cookie HttpOnly pe care
    Safari nu-l intoarce mereu la pagina urmatoare, iar fara copie bannerul
    revenea pe fiecare pagina. Expira dupa 12 luni, cand intrebam din nou.
  */
  remembered() {
    try {
      const raw = localStorage.getItem('amv-consent');
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (!item || !item.consent || Date.now() - item.at > 365 * 24 * 3600 * 1000) return null;
      return item.consent;
    } catch (e) {
      return null;
    }
  }

  remember(consent) {
    try {
      localStorage.setItem('amv-consent', JSON.stringify({ consent, at: Date.now() }));
    } catch (e) {
      /* stocare blocata: ramane alegerea din Shopify */
    }
  }

  // Mirror whatever the visitor already decided, so reopening the panel
  // shows their real state instead of an empty form.
  reflect() {
    if (!this.api || typeof this.api.currentVisitorConsent !== 'function') return;
    const c = this.api.currentVisitorConsent() || {};
    this.querySelectorAll('[data-amv-consent-cat]').forEach((box) => {
      box.checked = c[box.dataset.amvConsentCat] === 'yes';
    });
  }

  open(showDetail) {
    this.hidden = false;
    if (showDetail) {
      this.reflect();
      this.setDetail(true);
    }
    const first = this.querySelector('button');
    if (first && showDetail) first.focus();
  }

  setDetail(on) {
    if (!this.detail || !this.toggle) return;
    this.detail.hidden = !on;
    this.toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
  }

  onClick(event) {
    const t = event.target.closest('[data-amv-consent], [data-amv-consent-toggle]');
    if (!t) return;

    if (t.hasAttribute('data-amv-consent-toggle')) {
      this.setDetail(this.detail.hidden);
      return;
    }

    const mode = t.getAttribute('data-amv-consent');
    let consent;
    if (mode === 'all') {
      consent = { analytics: true, marketing: true, preferences: true, sale_of_data: true };
    } else if (mode === 'none') {
      consent = { analytics: false, marketing: false, preferences: false, sale_of_data: false };
    } else {
      consent = { analytics: false, marketing: false, preferences: false };
      this.querySelectorAll('[data-amv-consent-cat]').forEach((box) => {
        consent[box.dataset.amvConsentCat] = box.checked;
      });
      consent.sale_of_data = consent.marketing;
    }
    this.save(consent);
  }

  save(consent) {
    if (!this.api) return;
    this.remember(consent);
    this.api.setTrackingConsent(consent, () => {
      this.hidden = true;
      this.setDetail(false);
    });
    this.google(consent);
  }

  /*
    Consent Mode v2. Only an update — the `denied` default is set in the
    layout, before any Google tag can fire. If the shop has no Google tag,
    dataLayer.push is a no-op that nobody reads.
  */
  google(consent) {
    if (!this.hasAttribute('data-google-mode')) return;
    if (typeof window.gtag !== 'function') return;
    const yes = 'granted';
    const no = 'denied';
    window.gtag('consent', 'update', {
      ad_storage: consent.marketing ? yes : no,
      ad_user_data: consent.marketing ? yes : no,
      ad_personalization: consent.marketing ? yes : no,
      analytics_storage: consent.analytics ? yes : no,
      functionality_storage: consent.preferences ? yes : no,
      personalization_storage: consent.preferences ? yes : no,
    });
  }
}

if (!customElements.get('amv-consent')) customElements.define('amv-consent', AmvConsent);
